/**
 * `<BrandLogo />` — the single logo component for the whole SPA.
 *
 * Replaces six hand-inlined copies of the same glyph that had drifted into
 * THREE different purples (`#AD46FF` in TaskBar / MobileHeader / mobile nav /
 * the SVG assets, `#C580FF` in the share-link SSR wordmark, `#a855f7` in the
 * token layer). Phase 0 made `#a855f7` canonical, so the default glyph here is
 * filled from `--brand-primary` — which means it recolours with the brand
 * automatically, with no JS and no per-call-site prop.
 *
 * NOTE ON THE SHAPE: this is the PRISM mark the product actually ships (same
 * path as apps/web/public/favicon.svg). It is deliberately NOT the older
 * `LogoMark` component in ../components, which draws a rounded-rect "P" tile —
 * that was a brand-kit artifact with zero importers and never matched the
 * shipped chrome. Don't reunify them; they are different marks.
 *
 * Resolution order per variant:
 *   mark      → assets.compactLogo, else the inline prism
 *   wordmark  → assets.logo, else the inline prism + productName text
 *
 * The text fallback is what lets a brand ship with NO wordmark asset and still
 * render a correct lockup — it just uses its own name in the app font.
 */
import type { CSSProperties } from 'react'
import { useBrand } from './BrandProvider.js'

/** The prism glyph, viewBox 0 0 100 100. Single source of truth for the shape. */
const PRISM_PATH =
  'M 50 1.95 L 2.35 33.1 50 64.25 97.65 33.1 50 1.95 M 50.2 13.6 L 79.5 32.85 50.2 52.1 ' +
  '20.7 32.85 50.2 13.6 M 50.05 84.6 L 41.8 91.35 50.05 98.1 58.3 91.35 50.05 84.6 M 21.25 54.6 ' +
  'L 14.1 59.25 50.15 82.8 86.2 59.25 78.95 54.5 50 73.4 21.25 54.6 Z'

export interface BrandLogoProps {
  /** `mark` = glyph only (taskbar, tight headers). `wordmark` = glyph + name. */
  variant?: 'mark' | 'wordmark'
  className?: string
  /** Pixel size of the glyph. Omit to let `className` drive sizing (the
   *  common case in this codebase, e.g. `className="w-5 h-5"`). */
  size?: number
  style?: CSSProperties
  /**
   * Hide from assistive tech. Use when the logo sits next to a visible text
   * label that already names the product — otherwise a screen reader announces
   * the brand twice. Defaults to false (the logo is labelled with the product
   * name).
   */
  decorative?: boolean
}

/**
 * The inline default glyph, filled from the brand token.
 *
 * Exported so the branding editor can draw the *fallback* — what a surface
 * renders when a logo slot is empty — inside its "where this is used" mockups.
 * `<BrandLogo>` cannot serve that purpose there: it reads the PUBLISHED brand
 * from context, so on a brand that already has a logo it would show that image
 * instead of the empty-slot default the mockup is trying to illustrate.
 */
export function PrismGlyph({ className, size, style, label, decorative }: {
  className?: string
  size?: number
  style?: CSSProperties
  label: string
  decorative: boolean
}) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      width={size}
      height={size}
      style={style}
      // `var(--brand-primary)` rather than a literal: the pre-paint script sets
      // it before first paint on a warm cache, so the glyph is already the right
      // colour on the very first frame — no flash of the previous brand.
      fill="var(--brand-primary, #a855f7)"
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
    >
      <path d={PRISM_PATH} />
    </svg>
  )
}

export function BrandLogo({
  variant = 'mark',
  className,
  size,
  style,
  decorative = false,
}: BrandLogoProps) {
  const { brand, productName } = useBrand()
  const asset = variant === 'wordmark' ? brand.assets.logo : brand.assets.compactLogo

  if (asset) {
    return (
      <img
        src={asset}
        // A branded asset still needs the product name as alt text; an empty
        // alt is only correct when the image is genuinely decorative.
        alt={decorative ? '' : productName}
        className={className}
        width={size}
        height={size}
        style={style}
      />
    )
  }

  if (variant === 'wordmark') {
    // No wordmark asset: compose one from the glyph + the brand's own name.
    // The glyph is marked decorative here because the adjacent text already
    // announces the product.
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em', ...style }}>
        <PrismGlyph size={size ?? 24} label={productName} decorative />
        <span>{productName}</span>
      </span>
    )
  }

  return (
    <PrismGlyph
      className={className}
      size={size}
      style={style}
      label={productName}
      decorative={decorative}
    />
  )
}
